import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2, Edit3, Save, GripVertical, Repeat, Clock } from 'lucide-react';
import api from '../utils/api';


const PersonalTasksSettings = ({ isOpen, onClose, user }) => {
  const [activeTab, setActiveTab] = useState('stages');
  const [stages, setStages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingStage, setEditingStage] = useState(null);
  const [newStageName, setNewStageName] = useState('');
  const [newStageColor, setNewStageColor] = useState('#6B7280');

  useEffect(() => {
    if (isOpen) {
      loadStages();
    }
  }, [isOpen]);

  const loadStages = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      
      const response = await api.get(`/personal-tasks/stages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setStages(response.data);
    } catch (error) {
      console.error('Error loading stages:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateStage = async () => {
    if (!newStageName.trim()) return;
    
    try {
      const token = localStorage.getItem('access_token');
      
      await api.post(
        `/personal-tasks/stages`,
        { name: newStageName, color: newStageColor },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setNewStageName('');
      setNewStageColor('#6B7280');
      await loadStages();
    } catch (error) {
      console.error('Error creating stage:', error);
      alert('Failed to create stage');
    }
  };

  const handleUpdateStage = async (stageId, updates) => {
    try {
      const token = localStorage.getItem('access_token');
      
      await api.put(
        `/personal-tasks/stages/${stageId}`,
        updates,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      await loadStages();
      setEditingStage(null);
    } catch (error) {
      console.error('Error updating stage:', error);
      alert('Failed to update stage');
    }
  };

  const handleDeleteStage = async (stageId) => {
    if (!window.confirm('Are you sure? Tasks in this stage will be moved to the first stage.')) return;
    
    try {
      const token = localStorage.getItem('access_token');
      
      await api.delete(`/personal-tasks/stages/${stageId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      await loadStages();
    } catch (error) {
      console.error('Error deleting stage:', error);
      alert('Failed to delete stage');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-purple-600 to-purple-700">
          <h2 className="text-xl font-bold text-white">Personal Tasks Settings</h2>
          <button
            onClick={onClose}
            className="text-white hover:bg-purple-800 rounded-lg p-2 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200">
          <button
            onClick={() => setActiveTab('stages')}
            className={`px-6 py-3 font-medium transition-colors ${
              activeTab === 'stages'
                ? 'text-purple-600 border-b-2 border-purple-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Stages
          </button>
          <button
            onClick={() => setActiveTab('recurring')}
            className={`px-6 py-3 font-medium transition-colors ${
              activeTab === 'recurring'
                ? 'text-purple-600 border-b-2 border-purple-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Recurring Templates
          </button>
          <button
            onClick={() => setActiveTab('hours')}
            className={`px-6 py-3 font-medium transition-colors ${
              activeTab === 'hours'
                ? 'text-purple-600 border-b-2 border-purple-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Personal Hours
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
          {activeTab === 'stages' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Manage Stages</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Customize your personal kanban board stages. Drag to reorder.
                </p>

                {/* Existing Stages */}
                <div className="space-y-2 mb-6">
                  {stages.map((stage) => (
                    <div
                      key={stage.id}
                      className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200"
                    >
                      <GripVertical className="w-5 h-5 text-gray-400 cursor-move" />
                      
                      <div
                        className="w-4 h-4 rounded-full flex-shrink-0"
                        style={{ backgroundColor: stage.color }}
                      />
                      
                      {editingStage === stage.id ? (
                        <>
                          <input
                            type="text"
                            defaultValue={stage.name}
                            className="flex-1 px-2 py-1 border border-gray-300 rounded"
                            id={`edit-${stage.id}`}
                          />
                          <input
                            type="color"
                            defaultValue={stage.color}
                            className="w-10 h-8 rounded cursor-pointer"
                            id={`color-${stage.id}`}
                          />
                          <button
                            onClick={() => {
                              const name = document.getElementById(`edit-${stage.id}`).value;
                              const color = document.getElementById(`color-${stage.id}`).value;
                              handleUpdateStage(stage.id, { name, color });
                            }}
                            className="p-2 text-green-600 hover:bg-green-50 rounded"
                          >
                            <Save className="w-4 h-4" />
                          </button>
                        </>
                      ) : (
                        <>
                          <span className="flex-1 font-medium text-gray-900">{stage.name}</span>
                          <button
                            onClick={() => setEditingStage(stage.id)}
                            className="p-2 text-gray-600 hover:bg-gray-200 rounded"
                          >
                            <Edit3 className="w-4 h-4" />
                          </button>
                          {stages.length > 1 && (
                            <button
                              onClick={() => handleDeleteStage(stage.id)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  ))}
                </div>

                {/* Add New Stage */}
                <div className="flex items-center gap-3 p-3 bg-purple-50 rounded-lg border-2 border-dashed border-purple-300">
                  <Plus className="w-5 h-5 text-purple-600" />
                  <input
                    type="text"
                    placeholder="New stage name"
                    value={newStageName}
                    onChange={(e) => setNewStageName(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleCreateStage()}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                  <input
                    type="color"
                    value={newStageColor}
                    onChange={(e) => setNewStageColor(e.target.value)}
                    className="w-12 h-10 rounded cursor-pointer"
                  />
                  <button
                    onClick={handleCreateStage}
                    disabled={!newStageName.trim()}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Add
                  </button>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'recurring' && (
            <div className="text-center py-12">
              <Repeat className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Recurring Task Templates</h3>
              <p className="text-gray-600">
                Manage your personal recurring task templates here.
              </p>
              <p className="text-sm text-gray-500 mt-2">
                (Feature coming soon - you can create recurring templates from regular tasks)
              </p>
            </div>
          )}

          {activeTab === 'hours' && (
            <div className="text-center py-12">
              <Clock className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Personal Work Hours</h3>
              <p className="text-gray-600">
                Configure your personal work hours for task scheduling.
              </p>
              <p className="text-sm text-gray-500 mt-2">
                (Feature coming soon - set different hours than your team settings)
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default PersonalTasksSettings;
